import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types';
import toast from 'react-hot-toast';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Get the current domain for auth callback
const domain = import.meta.env.VITE_DOMAIN || window.location.host;
const redirectUrl = `https://${domain}/auth/v1/callback`;

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    redirectTo: redirectUrl,
    flowType: 'pkce',
    debug: true,
    storage: {
      getItem: (key) => {
        try {
          return Promise.resolve(localStorage.getItem(key));
        } catch {
          return Promise.resolve(null);
        }
      },
      setItem: (key, value) => {
        try {
          localStorage.setItem(key, value);
          return Promise.resolve();
        } catch {
          return Promise.resolve();
        }
      },
      removeItem: (key) => {
        try {
          localStorage.removeItem(key);
          return Promise.resolve();
        } catch {
          return Promise.resolve();
        }
      }
    }
  }
});

// Initialize Supabase and verify connection
// We'll use anonymous access for public storage
export const initializeSupabase = async () => {
  try {
    // Test the connection
    const { error } = await supabase.auth.getSession();
    if (error) {
      console.error('Error connecting to Supabase:', error);
      return false;
    }
    
    // Check if bucket exists
    const { data: buckets, error: bucketsError } = await supabase
      .storage
      .listBuckets();
    
    if (bucketsError) {
      console.error('Error checking buckets:', bucketsError);
      return false;
    }
    
    const campaignBucketExists = buckets?.some(bucket => bucket.name === 'campaign-images');
    
    if (!campaignBucketExists) {
      console.warn('Campaign images bucket does not exist, attempting to create it...');
      
      try {
        // Try to create the bucket
        const { error: createError } = await supabase
          .storage
          .createBucket('campaign-images', {
            public: true,
            fileSizeLimit: 5242880 // 5MB
          });
          
        if (createError) {
          console.error('Error creating bucket:', createError);
          return false;
        }
        
        // Set public bucket policy
        const { error: policyError } = await supabase
          .storage
          .from('campaign-images')
          .createSignedUrl('dummy.txt', 60);
          
        if (policyError && !policyError.message.includes('does not exist')) {
          console.error('Error setting bucket policy:', policyError);
        }
        
        return true;
      } catch (createError) {
        console.error('Failed to create storage bucket:', createError);
        return false;
      }
    }
    
    return true;
  } catch (error) {
    console.error('Failed to initialize Supabase:', error);
    return false;
  }
};

// Get current authenticated user
export const getCurrentUser = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) {
      console.error('Error getting user:', error);
      return null;
    }
    return user;
  } catch (error) {
    console.error('Error getting user:', error);
    return null;
  }
};